import { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { CANADIAN_LOCATIONS } from '@/lib/canadianLocations';

export function LocationSelect({ value, onChange, placeholder = "All Provinces", className = "", testId = "location-select" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedProvince, setExpandedProvince] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setExpandedProvince(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (province, city = '') => {
    onChange(province, city);
    setIsOpen(false);
    setExpandedProvince(null);
  };

  const toggleProvince = (provinceName) => {
    if (expandedProvince === provinceName) {
      setExpandedProvince(null);
    } else {
      setExpandedProvince(provinceName);
    }
  };

  const getDisplayValue = () => {
    if (!value.province) return placeholder;
    if (value.city) return `${value.city}, ${value.province}`;
    return value.province;
  };

  return (
    <div className="relative z-50" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between w-full h-10 px-3 text-sm rounded-xl border border-gray-200 bg-gray-50 text-[#1A1D20] transition-colors hover:bg-gray-100 focus:outline-none focus:border-[#4A90E2] focus:ring-2 focus:ring-blue-100 ${className}`}
        data-testid={testId}
      >
        <span className={!value.province ? 'text-gray-500' : ''}>{getDisplayValue()}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-[9999] w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-[400px] overflow-y-auto">
          <button
            type="button"
            onClick={() => handleSelect('', '')}
            className={`block w-full px-3 py-2 text-left text-sm transition-colors hover:bg-blue-50 ${!value.province ? 'bg-blue-50 font-semibold text-[#4A90E2]' : 'text-[#1A1D20]'}`}
          >
            {placeholder}
          </button>
          
          {CANADIAN_LOCATIONS.map((provinceInfo) => (
            <div key={provinceInfo.province}>
              <button
                type="button"
                onClick={() => toggleProvince(provinceInfo.province)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-blue-50 ${value.province === provinceInfo.province ? 'bg-blue-50 font-semibold text-[#4A90E2]' : 'text-[#1A1D20]'}`}
              >
                {expandedProvince === provinceInfo.province ? (
                  <ChevronDown className="w-3.5 h-3.5 shrink-0 text-gray-500" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 shrink-0 text-gray-500" />
                )}
                <span className="flex-1">{provinceInfo.province}</span>
              </button>

              {expandedProvince === provinceInfo.province && (
                <div className="bg-gray-50">
                  <button
                    type="button"
                    onClick={() => handleSelect(provinceInfo.province, '')}
                    className={`block w-full pl-10 pr-3 py-1.5 text-left text-xs transition-colors hover:bg-blue-50 ${value.province === provinceInfo.province && !value.city ? 'bg-blue-50 font-semibold text-[#4A90E2]' : 'text-[#5C636A]'}`}
                  >
                    All Cities in {provinceInfo.province}
                  </button>
                  {provinceInfo.cities.map((city) => (
                    <button
                      key={city}
                      type="button"
                      onClick={() => handleSelect(provinceInfo.province, city)}
                      className={`block w-full pl-10 pr-3 py-1.5 text-left text-xs transition-colors hover:bg-blue-50 ${value.province === provinceInfo.province && value.city === city ? 'bg-blue-50 font-semibold text-[#4A90E2]' : 'text-[#1A1D20]'}`}
                    >
                      {city}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
